import { useState } from 'react';
import { useEditorStore } from '../store/editor-store';
import type { TextCurve, TextObject } from '@ox/schema';
import { EDITOR_FONTS, TEXT_SWATCHES, swatchLabel } from '../lib/text-ui';
import { swatchStyle } from '../lib/swatchStyle';
import { defaultCurveRadiusMm } from '../lib/textCurve';
import { measureTextBoxMm } from '../lib/textMeasure';
import { CustomColorSwatch } from './CustomColorSwatch';
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBringForward,
  IconCenterH,
  IconCenterV,
  IconSendBackward,
} from './Icons';

type Mode = 'edit' | 'color' | 'font';

function applyCurve(obj: TextObject, curve: TextCurve, updateObject: (id: string, patch: Partial<TextObject>) => void) {
  if (curve === 'none') {
    updateObject(obj.id, { curve: 'none' });
    return;
  }
  const straight = measureTextBoxMm({
    content: obj.content,
    fontFamily: obj.fontFamily,
    fontSize: obj.fontSize,
    fontWeight: obj.fontWeight,
    lineHeight: obj.lineHeight,
    curve: 'none',
  });
  const radius =
    obj.curve !== 'none' && obj.curveRadius
      ? obj.curveRadius
      : defaultCurveRadiusMm(straight.width, obj.fontSize);
  updateObject(obj.id, { curve, curveRadius: radius });
}

export function TextPanel() {
  const selectedId = useEditorStore((s) => s.selectedId);
  const document = useEditorStore((s) => s.document);
  const activeView = useEditorStore((s) => s.activeView);
  const updateObject = useEditorStore((s) => s.updateObject);
  const centerH = useEditorStore((s) => s.centerH);
  const centerV = useEditorStore((s) => s.centerV);
  const bringForward = useEditorStore((s) => s.bringForward);
  const sendBackward = useEditorStore((s) => s.sendBackward);
  const deleteSelected = useEditorStore((s) => s.deleteSelected);
  const select = useEditorStore((s) => s.select);
  const [mode, setMode] = useState<Mode>('edit');

  const obj = document.views
    .find((v) => v.viewId === activeView)
    ?.objects.find((o) => o.id === selectedId && o.type === 'text') as TextObject | undefined;

  if (!obj) return null;

  if (mode === 'color') {
    return (
      <div className="ox-side-panel">
        <div className="ox-panel-head">
          <button type="button" className="ox-panel-back" onClick={() => setMode('edit')}>
            ← Atrás
          </button>
          <h3>Color del texto</h3>
        </div>
        <p className="ox-m-color-label">
          <strong>{swatchLabel(obj.color)}</strong>
        </p>
        <div className="ox-swatch-grid">
          {TEXT_SWATCHES.map((s) => (
            <button
              key={s.hex}
              type="button"
              title={s.label}
              className={`ox-swatch-btn${obj.color.toLowerCase() === s.hex.toLowerCase() ? ' active' : ''}`}
              style={swatchStyle(s.hex, {
                borderColor: s.hex === '#ffffff' ? '#ddd' : s.hex,
              })}
              onClick={() => updateObject(obj.id, { color: s.hex })}
            />
          ))}
          <CustomColorSwatch
            color={obj.color}
            onColor={(hex) => updateObject(obj.id, { color: hex })}
          />
        </div>
      </div>
    );
  }

  if (mode === 'font') {
    return (
      <div className="ox-side-panel ox-add-text-panel">
        <div className="ox-panel-head">
          <button type="button" className="ox-panel-back" onClick={() => setMode('edit')}>
            ← Atrás
          </button>
          <h3>Elegir fuente</h3>
        </div>
        <ul className="ox-font-list">
          {EDITOR_FONTS.map((f) => (
            <li key={f.label}>
              <button
                type="button"
                className={`ox-font-item${obj.fontFamily === f.family ? ' active' : ''}`}
                onClick={() => {
                  updateObject(obj.id, { fontFamily: f.family });
                  setMode('edit');
                }}
              >
                <span className="ox-font-preview" style={{ fontFamily: f.family }}>
                  {obj.content || 'Texto'}
                </span>
                <span className="ox-font-name">{f.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const fontLabel =
    EDITOR_FONTS.find((f) => f.family === obj.fontFamily)?.label ?? obj.fontFamily;

  return (
    <div className="ox-side-panel">
      <div className="ox-panel-head">
        <h3>Editar texto</h3>
        <button
          type="button"
          className="ox-panel-close"
          onClick={() => select(null)}
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>
      <label>
        Texto
        <textarea
          maxLength={200}
          value={obj.content}
          onChange={(e) => updateObject(obj.id, { content: e.target.value })}
        />
        <span className="ox-counter">
          {obj.content.length} / 200
        </span>
      </label>

      <button type="button" className="ox-row-nav" onClick={() => setMode('color')}>
        <span className="ox-mini-swatch" style={swatchStyle(obj.color)} />
        Color del texto
        <span className="ox-row-chevron">›</span>
      </button>

      <button type="button" className="ox-row-nav" onClick={() => setMode('font')}>
        <span className="ox-row-font-sample" style={{ fontFamily: obj.fontFamily }}>
          Aa
        </span>
        Fuente: {fontLabel}
        <span className="ox-row-chevron">›</span>
      </button>

      <div className="ox-align-row" role="group" aria-label="Alineación">
        <button
          type="button"
          title="Izquierda"
          className={obj.align === 'left' ? 'active' : ''}
          onClick={() => updateObject(obj.id, { align: 'left' })}
        >
          <IconAlignLeft />
        </button>
        <button
          type="button"
          title="Centro"
          className={obj.align === 'center' ? 'active' : ''}
          onClick={() => updateObject(obj.id, { align: 'center' })}
        >
          <IconAlignCenter />
        </button>
        <button
          type="button"
          title="Derecha"
          className={obj.align === 'right' ? 'active' : ''}
          onClick={() => updateObject(obj.id, { align: 'right' })}
        >
          <IconAlignRight />
        </button>
      </div>

      <p className="ox-section-label">Texto curvo</p>
      <div className="ox-curve-presets" role="group" aria-label="Curva del texto">
        <button
          type="button"
          title="Recto"
          className={(obj.curve ?? 'none') === 'none' ? 'active' : ''}
          onClick={() => applyCurve(obj, 'none', updateObject)}
        >
          <span className="ox-curve-ico ox-curve-ico--flat">ABCD</span>
        </button>
        <button
          type="button"
          title="Arco arriba"
          className={(obj.curve ?? 'none') === 'arc-up' ? 'active' : ''}
          onClick={() => applyCurve(obj, 'arc-up', updateObject)}
        >
          <span className="ox-curve-ico ox-curve-ico--up">ABCD</span>
        </button>
        <button
          type="button"
          title="Arco abajo"
          className={(obj.curve ?? 'none') === 'arc-down' ? 'active' : ''}
          onClick={() => applyCurve(obj, 'arc-down', updateObject)}
        >
          <span className="ox-curve-ico ox-curve-ico--down">ABCD</span>
        </button>
      </div>
      {(obj.curve ?? 'none') !== 'none' && (() => {
        const radiusMin = Math.max(8, Math.round(obj.fontSize * 0.35));
        const radiusMax = 500;
        const radiusVal = Math.round(obj.curveRadius ?? 80);
        return (
          <label className="ox-curve-radius">
            Radio
            <span className="ox-hint" style={{ display: 'block', fontSize: 11, opacity: 0.7 }}>
              Menor = más curvo
            </span>
            <div className="ox-curve-radius-row">
              <input
                type="range"
                min={radiusMin}
                max={radiusMax}
                step={1}
                value={Math.min(radiusMax, Math.max(radiusMin, radiusVal))}
                onChange={(e) =>
                  updateObject(obj.id, {
                    curveRadius: Math.max(radiusMin, Number(e.target.value) || radiusMin),
                  })
                }
              />
              <input
                type="number"
                min={radiusMin}
                max={radiusMax}
                value={radiusVal}
                onChange={(e) =>
                  updateObject(obj.id, {
                    curveRadius: Math.max(radiusMin, Number(e.target.value) || radiusMin),
                  })
                }
              />
            </div>
          </label>
        );
      })()}

      <label>
        Tamaño
        <div className="ox-stepper">
          <button
            type="button"
            onClick={() => updateObject(obj.id, { fontSize: Math.max(4, obj.fontSize - 1) })}
          >
            −
          </button>
          <input
            type="number"
            value={Math.round(obj.fontSize)}
            onChange={(e) =>
              updateObject(obj.id, { fontSize: Math.max(4, Number(e.target.value) || 4) })
            }
          />
          <button
            type="button"
            onClick={() => updateObject(obj.id, { fontSize: obj.fontSize + 1 })}
          >
            +
          </button>
        </div>
      </label>

      <div className="ox-icon-actions" role="group" aria-label="Posición">
        <button type="button" title="Centrar horizontal" onClick={centerH}>
          <IconCenterH />
          <span>Centrar H</span>
        </button>
        <button type="button" title="Centrar vertical" onClick={centerV}>
          <IconCenterV />
          <span>Centrar V</span>
        </button>
        <button type="button" title="Adelante" onClick={bringForward}>
          <IconBringForward />
          <span>Adelante</span>
        </button>
        <button type="button" title="Atrás" onClick={sendBackward}>
          <IconSendBackward />
          <span>Atrás</span>
        </button>
      </div>

      <button type="button" className="ox-danger-full" onClick={deleteSelected}>
        Borrar
      </button>
    </div>
  );
}
